import React, { useEffect, useContext, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useOktaAuth, LoginCallback } from "@okta/okta-react";

import DashboardLayout from "@/layouts/Dashboard";
import SecureRoute from "./SecureRoute/SecureRoute";
import Loader from "@/components/Loader";
import AuthorizedRoute from "@/router/AuthorizedRoute/AuthorizedRoute";
import { useConfig } from "@/contexts/config";
import { pagesConfig, MenuItem } from "@/router/pagesConfig";
import { NEEDED_RIGHTS as CONTRACT_NEEDED_RIGHTS } from "@/views/Contracts/neededRights";
import { UserContext } from "@/contexts/user";
import userApi from "@/api/userApi";

const Router: React.FC = () => {
  const [, dispatch] = useContext<any>(UserContext);
  const [loading, setLoading] = useState<boolean>(true);
  const { t } = useTranslation();
  const { config } = useConfig();
  const { oktaAuth, authState } = useOktaAuth();
  const { enqueueSnackbar } = useSnackbar();

  const setUser = (payload: {}) => {
    dispatch({ type: "SET_USER", payload });
  };

  const getUser = async () => {
    setLoading(true);
    try {
      const userInfo = await oktaAuth.token?.getUserInfo();
      const currentUser = await userApi.getExternalUser();
      setUser({ ...userInfo, ...currentUser });
    } catch (err: any) {
      enqueueSnackbar(err.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authState?.isAuthenticated) getUser();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState?.isAuthenticated]);

  if (loading) {
    return (
      <Routes>
        <Route path="/" element={<SecureRoute />} />
        <Route
          path="login/callback"
          element={<LoginCallback loadingElement={<Loader />} />}
        />
        {authState && !authState?.isAuthenticated ? (
          <Route path="*" element={<Navigate to="/" />} />
        ) : (
          <Route path="*" element={<Loader />} />
        )}
      </Routes>
    );
  }

  const renderRoutes = (items: MenuItem[]): React.ReactNode[] =>
    items.flatMap((e, i) => {
      const routes: React.ReactNode[] = [];

      if (!("subItems" in e))
        routes.push(<Route key={i} path={e.link} element={e.component} />);
      if ("subItems" in e && e.subItems.length > 0)
        routes.push(...renderRoutes(e.subItems));

      return routes;
    });

  return (
    <Routes>
      <Route path="/" element={<SecureRoute />}>
        <Route path="" element={<DashboardLayout />}>
          <Route
            path=""
            element={<AuthorizedRoute neededRights={CONTRACT_NEEDED_RIGHTS} />}
          >
            {renderRoutes(pagesConfig(t, config))}
          </Route>
          <Route path="*" element={<Navigate to="/" />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default Router;
